import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageSquare, ArrowLeft, Users, Bot, DollarSign, Settings, Clock, AlertTriangle, Loader2, Trash2, UserPlus, Shield, Copy, Check, CalendarPlus, Ban, Infinity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [incompleteRegistrations, setIncompleteRegistrations] = useState([]);
  const [waitingList, setWaitingList] = useState([]);
  const [betaTesters, setBetaTesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxBots, setMaxBots] = useState(50);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Beta tester form state
  const [betaEmail, setBetaEmail] = useState('');
  const [betaPlan, setBetaPlan] = useState('basic');
  const [betaDays, setBetaDays] = useState(30);
  const [betaPermanent, setBetaPermanent] = useState(false);
  const [creatingBeta, setCreatingBeta] = useState(false);
  const [lastCreatedCode, setLastCreatedCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);

  // Extend modal state
  const [extendEmail, setExtendEmail] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);

  // Delete user state
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmDeleteEmail, setConfirmDeleteEmail] = useState(null);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Admin bots management state
  const [allBots, setAllBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingBot, setSavingBot] = useState(false);
  const [botSearch, setBotSearch] = useState('');

  // Partner invite codes state
  const [partnerCodes, setPartnerCodes] = useState([]);
  const [partnerCodesLoading, setPartnerCodesLoading] = useState(false);
  const [newCodeNote, setNewCodeNote] = useState('');
  const [newCodeMaxBots, setNewCodeMaxBots] = useState(1000);
  const [creatingCode, setCreatingCode] = useState(false);
  const [copiedPartnerCode, setCopiedPartnerCode] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, usersRes, waitRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/waiting-list`, { withCredentials: true }),
      ]);
      setStats(dashRes.data);
      setUsers(usersRes.data.users || []);
      setIncompleteRegistrations(usersRes.data.incomplete_registrations || []);
      setWaitingList(waitRes.data.waiting_list || []);
      setMaxBots(dashRes.data.settings?.max_active_bots || 50);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Acceso denegado. Solo administradores.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchBetaTesters = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/beta-testers`, { withCredentials: true });
      setBetaTesters(res.data.beta_testers || []);
    } catch (e) {
      console.error('Error fetching beta testers:', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'beta') fetchBetaTesters(); }, [activeTab, fetchBetaTesters]);

  const fetchPartnerCodes = useCallback(async () => {
    setPartnerCodesLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/partner/invite-codes`, { withCredentials: true });
      setPartnerCodes(res.data.codes || []);
    } catch (e) {
      console.error('Error fetching partner codes:', e);
    } finally {
      setPartnerCodesLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTab === 'partners') fetchPartnerCodes(); }, [activeTab, fetchPartnerCodes]);

  const createPartnerCode = async () => {
    setCreatingCode(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/partner/invite-codes`,
        { note: newCodeNote.trim(), max_bots: Number(newCodeMaxBots) || 1000 },
        { withCredentials: true });
      toast.success(`Código creado: ${r.data.code}`);
      setNewCodeNote('');
      setNewCodeMaxBots(1000);
      fetchPartnerCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear código');
    } finally {
      setCreatingCode(false);
    }
  };

  const revokePartnerCode = async (code) => {
    if (!window.confirm(`¿Revocar el código ${code}? Si está sin usar, dejará de funcionar.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/partner/invite-codes/${encodeURIComponent(code)}`,
        { withCredentials: true });
      toast.success('Código revocado');
      fetchPartnerCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al revocar');
    }
  };

  const copyPartnerCode = (code) => {
    const url = `${window.location.origin}/register-partner?code=${code}`;
    navigator.clipboard.writeText(url);
    setCopiedPartnerCode(code);
    toast.success('Link de invitación copiado');
    setTimeout(() => setCopiedPartnerCode(null), 2000);
  };

  const fetchAllBots = useCallback(async () => {
    setBotsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/bots`, { withCredentials: true });
      setAllBots(res.data.bots || []);
    } catch (e) {
      console.error('Error fetching bots:', e);
    } finally {
      setBotsLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTab === 'bots') fetchAllBots(); }, [activeTab, fetchAllBots]);

  const handleEditBot = (bot) => {
    setEditingBot(bot.bot_id);
    setEditForm({
      bot_prompt: bot.bot_prompt || '',
      business_name: bot.business_name || '',
      services: bot.services || '',
      pricing: bot.pricing || '',
      working_hours: bot.working_hours || '',
      welcome_message: bot.welcome_message || '',
    });
  };

  const handleSaveBot = async () => {
    setSavingBot(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/bots/update`, { bot_id: editingBot, ...editForm }, { withCredentials: true });
      toast.success('Bot actualizado');
      setEditingBot(null);
      fetchAllBots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingBot(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/settings`, { max_active_bots: maxBots }, { withCredentials: true });
      toast.success('Configuracion guardada');
      fetchData();
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRemoveFromWaitlist = async (email) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/remove-from-waitlist`, { email }, { withCredentials: true });
      toast.success('Removido de la lista de espera');
      fetchData();
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleCreateBetaTester = async () => {
    setCreatingBeta(true);
    setLastCreatedCode(null);
    try {
      const payload = {
        plan_id: betaPlan,
        duration_days: betaDays,
        is_permanent: betaPermanent,
      };
      if (betaEmail.trim()) payload.email = betaEmail.trim();
      const res = await axios.post(`${BACKEND_URL}/api/admin/beta-testers`, payload, { withCredentials: true });
      const code = res.data.invite_code?.code;
      setLastCreatedCode(code);
      if (betaEmail.trim()) {
        toast.success(`Beta activado para ${betaEmail} con codigo ${code}`);
      } else {
        toast.success(`Codigo de invitacion creado: ${code}`);
      }
      setBetaEmail('');
      setBetaPermanent(false);
      setBetaDays(30);
      fetchBetaTesters();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear beta tester');
    } finally {
      setCreatingBeta(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Codigo copiado');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleExtendBeta = async () => {
    if (!extendEmail) return;
    setExtending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/beta-testers/extend`, { email: extendEmail, extra_days: extendDays }, { withCredentials: true });
      toast.success(`Acceso extendido ${extendDays} dias`);
      setExtendEmail(null);
      fetchBetaTesters();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al extender');
    } finally {
      setExtending(false);
    }
  };

  const handleRevokeBeta = async (email) => {
    if (!window.confirm(`Revocar acceso beta para ${email}?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/beta-testers/revoke`, { email }, { withCredentials: true });
      toast.success('Acceso beta revocado');
      fetchBetaTesters();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al revocar');
    }
  };

  const handleDeleteBetaCode = async (code) => {
    if (!window.confirm(`Eliminar codigo ${code}? Si fue usado, tambien se revocara el acceso.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/beta-testers/${code}`, { withCredentials: true });
      toast.success('Codigo eliminado');
      fetchBetaTesters();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleDeleteUser = async (email) => {
    setDeletingUser(email);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`Usuario ${email} eliminado`);
      setConfirmDeleteEmail(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeletingUser(null);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/prompt-audit-log`, { withCredentials: true });
      setAuditLogs(data.logs || []);
    } catch (e) {
      console.error('Error fetching audit logs:', e);
    }
    setAuditLoading(false);
  };

  const getSubBadge = (sub) => {
    if (!sub) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Sin plan</span>;
    const styles = { active: 'bg-emerald-100 text-emerald-700', trialing: 'bg-blue-100 text-blue-700', expired: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-700' };
    const labels = { active: 'Activa', trialing: 'Prueba', expired: 'Expirada', cancelled: 'Cancelada' };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[sub.status] || 'bg-gray-100 text-gray-600'}`}>{labels[sub.status] || sub.status} - {sub.plan_id}</span>;
  };

  const getPlanLabel = (pid) => ({ basic: 'Basico', pro: 'Pro', agency: 'Agencia', business: 'Business' }[pid] || pid);
  const getBetaStatusBadge = (entry) => {
    if (!entry.used_by) {
      if (entry.reserved_for) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">Reservado</span>;
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Sin usar</span>;
    }
    const sub = entry.subscription;
    if (!sub) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Sin sub</span>;
    if (sub.status === 'cancelled') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Revocado</span>;
    if (sub.status === 'expired') return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Expirado</span>;
    if (entry.is_permanent) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Permanente</span>;
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Activo</span>;
  };

  const getDaysRemaining = (entry) => {
    if (!entry.used_by || !entry.subscription) return '-';
    if (entry.is_permanent) return 'Ilimitado';
    const end = new Date(entry.subscription.current_period_end);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Expirado';
    return `${diff} dias`;
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  if (!stats) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="admin-back-btn"><ArrowLeft className="w-5 h-5 mr-2" />Dashboard</Button>
          </div>
          <div className="flex items-center gap-2"><MessageSquare className="w-7 h-7 text-indigo-600" /><span className="text-xl font-bold text-gray-950">Admin Panel</span></div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold tracking-tight text-gray-950 mb-2">Panel de Administrador</h1>
        <p className="text-gray-600 mb-8">Gestion completa de la plataforma</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-200 pb-2 overflow-x-auto">
          {[['overview', 'Resumen'], ['users', 'Usuarios'], ['beta', 'Beta Testers'], ['partners', 'Partners'], ['bots', 'Bots'], ['capacity', 'Capacidad'], ['audit', 'Auditoria']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} data-testid={`admin-tab-${key}`}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{label}</button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
              {[
                { label: 'Usuarios Totales', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
                { label: 'Nuevos Este Mes', value: stats.new_users_month, icon: Users, color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Ingresos del Mes', value: `$${stats.monthly_revenue.toFixed(2)}`, icon: DollarSign, color: 'text-purple-600 bg-purple-50' },
                { label: 'Bots Activos', value: stats.active_bots, icon: Bot, color: 'text-amber-600 bg-amber-50' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}><item.icon className="w-5 h-5" /></div>
                    <span className="text-sm text-gray-500">{item.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-950">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="subscription-stats">
                <h3 className="text-lg font-semibold text-gray-950 mb-4">Suscripciones</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Activas', value: stats.subscriptions.active, color: 'bg-emerald-500' },
                    { label: 'En Prueba', value: stats.subscriptions.trialing, color: 'bg-blue-500' },
                    { label: 'Expiradas', value: stats.subscriptions.expired, color: 'bg-red-500' },
                    { label: 'Canceladas', value: stats.subscriptions.cancelled, color: 'bg-gray-400' },
                    { label: 'Con Descuento', value: stats.subscriptions.discounted, color: 'bg-purple-500' },
                    { label: 'Pago Fallido', value: stats.subscriptions.payment_failed || 0, color: 'bg-orange-500' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full ${item.color}`}></div><span className="text-sm text-gray-700">{item.label}</span></div>
                      <span className="text-sm font-semibold text-gray-950">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="bot-stats">
                <h3 className="text-lg font-semibold text-gray-950 mb-4">Estado de Asistentes</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Conectados', value: stats.active_bots, color: 'bg-emerald-500' },
                    { label: 'Dormidos', value: stats.sleeping_bots, color: 'bg-amber-500' },
                    { label: 'Desconectados', value: stats.disconnected_bots, color: 'bg-gray-400' },
                    { label: 'Total', value: stats.total_bots, color: 'bg-indigo-500' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full ${item.color}`}></div><span className="text-sm text-gray-700">{item.label}</span></div>
                      <span className="text-sm font-semibold text-gray-950">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Beta tester quick stats in overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="beta-overview-stats">
                <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-indigo-500" />Beta Testers</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Beta Testers Activos</span>
                    <span className="text-sm font-semibold text-gray-950">{stats.beta_testers || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Codigos Sin Usar</span>
                    <span className="text-sm font-semibold text-gray-950">{stats.unused_invite_codes || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Delete Confirmation Modal */}
            {confirmDeleteEmail && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-user-modal-overlay">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="delete-user-modal">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-950">Eliminar usuario</h3>
                      <p className="text-sm text-gray-500">Esta accion no se puede deshacer</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">Se eliminara permanentemente:</p>
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
                    <p className="text-sm font-mono text-red-800 mb-2">{confirmDeleteEmail}</p>
                    <ul className="text-xs text-red-700 space-y-1">
                      <li>Cuenta de usuario</li>
                      <li>Todos sus asistentes (bots)</li>
                      <li>Suscripcion y pagos</li>
                      <li>Configuraciones de WhatsApp</li>
                    </ul>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDeleteEmail(null)}
                      className="flex-1"
                      data-testid="cancel-delete-user-btn"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={() => handleDeleteUser(confirmDeleteEmail)}
                      disabled={deletingUser === confirmDeleteEmail}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-delete-user-btn"
                    >
                      {deletingUser === confirmDeleteEmail ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando...</>
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-2" />Eliminar</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="admin-users-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Nombre</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Suscripcion</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Asistentes</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Registro</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700 font-mono text-xs">{u.email}</td>
                        <td className="py-3 px-4 text-gray-700">{u.name || '-'}</td>
                        <td className="py-3 px-4">{getSubBadge(u.subscription)}</td>
                        <td className="py-3 px-4 text-gray-700">{u.bot_count}</td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-4">
                          {u.role !== 'admin' ? (
                            <button
                              onClick={() => setConfirmDeleteEmail(u.email)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                              title="Eliminar usuario"
                              data-testid={`delete-user-btn-${i}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">Admin</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* End Users Table */}
          </>
        )}

        {/* Beta Testers Tab */}
        {activeTab === 'beta' && (
          <div className="space-y-6">
            {/* Create Beta Tester Form */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="create-beta-form">
              <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-500" />Crear Beta Tester
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label className="text-gray-700 font-medium text-sm mb-1.5 block">Email del usuario (opcional)</Label>
                  <Input
                    type="email"
                    value={betaEmail}
                    onChange={(e) => setBetaEmail(e.target.value)}
                    data-testid="beta-email-input"
                    className="bg-white border-gray-200 rounded-lg text-sm"
                    placeholder="usuario@email.com"
                  />
                  <p className="text-xs text-gray-400 mt-1">Vacio = solo genera codigo</p>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium text-sm mb-1.5 block">Plan asignado</Label>
                  <select
                    value={betaPlan}
                    onChange={(e) => setBetaPlan(e.target.value)}
                    data-testid="beta-plan-select"
                    className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="basic">Basico (1 asistente)</option>
                    <option value="pro">Pro (3 asistentes)</option>
                    <option value="agency">Agencia (10 asistentes)</option>
                    <option value="business">Business (10 asistentes + pedidos)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium text-sm mb-1.5 block">Duracion (dias)</Label>
                  <Input
                    type="number"
                    value={betaDays}
                    onChange={(e) => setBetaDays(parseInt(e.target.value) || 30)}
                    data-testid="beta-days-input"
                    className="bg-white border-gray-200 rounded-lg text-sm"
                    min={1}
                    max={365}
                    disabled={betaPermanent}
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="beta-permanent-toggle">
                    <input
                      type="checkbox"
                      checked={betaPermanent}
                      onChange={(e) => setBetaPermanent(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      <Infinity className="w-4 h-4 text-purple-500" /> Acceso permanente
                    </span>
                  </label>
                  <Button
                    onClick={handleCreateBetaTester}
                    disabled={creatingBeta}
                    data-testid="create-beta-btn"
                    className="bg-indigo-600 text-white hover:bg-indigo-700 w-full"
                  >
                    {creatingBeta ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    {betaEmail.trim() ? 'Activar Beta' : 'Generar Codigo'}
                  </Button>
                </div>
              </div>

              {/* Last created code display */}
              {lastCreatedCode && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between" data-testid="last-created-code">
                  <div>
                    <p className="text-sm text-indigo-700 font-medium">Codigo generado:</p>
                    <p className="text-2xl font-bold text-indigo-900 tracking-widest font-mono">{lastCreatedCode}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleCopyCode(lastCreatedCode)}
                    data-testid="copy-code-btn"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  >
                    {copiedCode === lastCreatedCode ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copiedCode === lastCreatedCode ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
              )}
            </div>

            {/* Extend modal */}
            {extendEmail && (
              <div className="bg-white border-2 border-indigo-300 rounded-xl p-6 shadow-md" data-testid="extend-beta-modal">
                <h3 className="text-lg font-semibold text-gray-950 mb-3 flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-indigo-500" />Extender acceso para {extendEmail}
                </h3>
                <div className="flex gap-3 items-end">
                  <div>
                    <Label className="text-gray-700 font-medium text-sm mb-1.5 block">Dias adicionales</Label>
                    <Input
                      type="number"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                      data-testid="extend-days-input"
                      className="bg-white border-gray-200 rounded-lg w-32"
                      min={1}
                    />
                  </div>
                  <Button onClick={handleExtendBeta} disabled={extending} data-testid="extend-confirm-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
                    {extending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Extender
                  </Button>
                  <Button variant="ghost" onClick={() => setExtendEmail(null)} data-testid="extend-cancel-btn" className="text-gray-600">
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* Beta Testers Table */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="beta-testers-table">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-950 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-500" />Beta Testers
                  <span className="text-sm font-normal text-gray-500">({betaTesters.length})</span>
                </h3>
              </div>
              {betaTesters.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay beta testers aun. Crea el primero arriba.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Codigo</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Plan</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Usuario</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Estado</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Dias Restantes</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Creado</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {betaTesters.map((entry, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded">{entry.code}</span>
                              <button onClick={() => handleCopyCode(entry.code)} className="text-gray-400 hover:text-indigo-600 transition-colors" data-testid={`copy-code-${entry.code}`}>
                                {copiedCode === entry.code ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{getPlanLabel(entry.plan_id)}</span>
                          </td>
                          <td className="py-3 px-4">
                            {entry.used_by ? (
                              <div>
                                <span className="text-gray-700 text-xs font-mono">{entry.used_by}</span>
                                {entry.user_name && <span className="text-gray-400 text-xs ml-1">({entry.user_name})</span>}
                              </div>
                            ) : entry.reserved_for ? (
                              <div>
                                <span className="text-indigo-600 text-xs font-mono">{entry.reserved_for}</span>
                                <span className="text-gray-400 text-xs ml-1">(reservado)</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs italic">Pendiente</span>
                            )}
                          </td>
                          <td className="py-3 px-4">{getBetaStatusBadge(entry)}</td>
                          <td className="py-3 px-4 text-gray-700 text-xs">{getDaysRemaining(entry)}</td>
                          <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(entry.created_at)}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {entry.used_by && entry.subscription?.status === 'active' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setExtendEmail(entry.used_by); setExtendDays(30); }}
                                    data-testid={`extend-beta-${entry.code}`}
                                    className="text-indigo-600 hover:bg-indigo-50 h-8 px-2"
                                    title="Extender acceso"
                                  >
                                    <CalendarPlus className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRevokeBeta(entry.used_by)}
                                    data-testid={`revoke-beta-${entry.code}`}
                                    className="text-red-600 hover:bg-red-50 h-8 px-2"
                                    title="Revocar acceso"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {!entry.used_by && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyCode(entry.code)}
                                  className="text-indigo-600 hover:bg-indigo-50 h-8 px-2"
                                  title="Copiar codigo"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBetaCode(entry.code)}
                                data-testid={`delete-beta-${entry.code}`}
                                className="text-red-600 hover:bg-red-50 h-8 px-2"
                                title="Eliminar codigo"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Partners Tab — Invite codes management */}
        {activeTab === 'partners' && (
          <div className="space-y-6" data-testid="admin-partners-tab">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-950 mb-1">Códigos de invitación para Partners</h3>
              <p className="text-sm text-gray-500 mb-4">
                Generá un código único y entregáselo al partner. Lo usa al registrarse en
                <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs mx-1">/register-partner</code>
                y queda con rol de partner automáticamente.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div className="sm:col-span-2">
                  <Label htmlFor="code-note">Nota interna (opcional)</Label>
                  <Input
                    id="code-note"
                    value={newCodeNote}
                    onChange={(e) => setNewCodeNote(e.target.value)}
                    placeholder="Ej: Agencia Ichiban — recomendado por Pablo"
                    data-testid="partner-code-note-input"
                  />
                </div>
                <div>
                  <Label htmlFor="code-max">Límite de bots</Label>
                  <Input
                    id="code-max"
                    type="number"
                    min={1}
                    max={10000}
                    value={newCodeMaxBots}
                    onChange={(e) => setNewCodeMaxBots(e.target.value)}
                    data-testid="partner-code-max-input"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button
                  onClick={createPartnerCode}
                  disabled={creatingCode}
                  data-testid="create-partner-code-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {creatingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Generar código
                </Button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="partner-codes-table">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h4 className="font-semibold text-gray-950 text-sm">Códigos generados ({partnerCodes.length})</h4>
              </div>
              {partnerCodesLoading ? (
                <div className="py-10 text-center text-gray-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : partnerCodes.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">Sin códigos generados todavía.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left">
                        <th className="px-4 py-2 font-medium text-gray-700">Código</th>
                        <th className="px-4 py-2 font-medium text-gray-700">Nota</th>
                        <th className="px-4 py-2 font-medium text-gray-700">Bots</th>
                        <th className="px-4 py-2 font-medium text-gray-700">Estado</th>
                        <th className="px-4 py-2 font-medium text-gray-700">Usado por</th>
                        <th className="px-4 py-2 font-medium text-gray-700 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerCodes.map((c) => (
                        <tr key={c.code} className="border-t border-gray-100" data-testid={`partner-code-row-${c.code}`}>
                          <td className="px-4 py-2 font-mono text-xs text-indigo-700">{c.code}</td>
                          <td className="px-4 py-2 text-gray-700">{c.note || '—'}</td>
                          <td className="px-4 py-2 text-gray-700">{c.max_bots}</td>
                          <td className="px-4 py-2">
                            {c.used ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                                Usado
                              </span>
                            ) : c.revoked ? (
                              <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                                Revocado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                                Disponible
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-gray-500 text-xs">
                            {c.used_by || '—'}
                            {c.used_at && <div className="text-[10px] text-gray-400">{new Date(c.used_at).toLocaleString()}</div>}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="inline-flex items-center gap-2">
                              {!c.used && !c.revoked && (
                                <button
                                  onClick={() => copyPartnerCode(c.code)}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded hover:bg-indigo-100"
                                  data-testid={`copy-partner-link-${c.code}`}
                                >
                                  {copiedPartnerCode === c.code
                                    ? <><Check className="w-3 h-3" /> Copiado</>
                                    : <><Copy className="w-3 h-3" /> Copiar link</>}
                                </button>
                              )}
                              {!c.used && (
                                <button
                                  onClick={() => revokePartnerCode(c.code)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  title="Revocar"
                                  data-testid={`revoke-partner-code-${c.code}`}
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              {c.used && (
                                <button
                                  onClick={() => revokePartnerCode(c.code)}
                                  className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                                  title="Eliminar del listado"
                                  data-testid={`delete-partner-code-${c.code}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Bots Management Tab */}
        {activeTab === 'bots' && (
          <div className="space-y-6" data-testid="admin-bots-tab">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-950">Todos los Bots ({allBots.length})</h3>
              <Input
                value={botSearch}
                onChange={(e) => setBotSearch(e.target.value)}
                placeholder="Buscar por email o nombre..."
                className="max-w-xs bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm"
                data-testid="bot-search-input"
              />
            </div>
            {botsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>
            ) : (
              <div className="space-y-3">
                {allBots
                  .filter(b => {
                    if (!botSearch.trim()) return true;
                    const q = botSearch.toLowerCase();
                    return (b.user_email || '').toLowerCase().includes(q) ||
                           (b.bot_name || '').toLowerCase().includes(q) ||
                           (b.business_name || '').toLowerCase().includes(q);
                  })
                  .map(bot => (
                  <div key={bot.bot_id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid={`admin-bot-${bot.bot_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-950">{bot.bot_name || 'Sin nombre'}</p>
                        <p className="text-sm text-gray-500">{bot.user_email}</p>
                        <p className="text-xs text-gray-400 font-mono">{bot.bot_id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${bot.status === 'connected' ? 'bg-emerald-500' : 'bg-gray-300'}`}></span>
                        <span className="text-xs text-gray-500">{bot.status || 'disconnected'}</span>
                      </div>
                    </div>

                    {editingBot === bot.bot_id ? (
                      <div className="space-y-3 border-t border-gray-100 pt-3" data-testid={`edit-bot-form-${bot.bot_id}`}>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Prompt del Asistente</Label>
                          <textarea value={editForm.bot_prompt} onChange={(e) => setEditForm({...editForm, bot_prompt: e.target.value})}
                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                            data-testid="edit-bot-prompt" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Nombre del Negocio</Label>
                            <Input value={editForm.business_name} onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                              className="bg-gray-50 border border-gray-200 text-sm" data-testid="edit-bot-business" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Servicios</Label>
                            <Input value={editForm.services} onChange={(e) => setEditForm({...editForm, services: e.target.value})}
                              className="bg-gray-50 border border-gray-200 text-sm" data-testid="edit-bot-services" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Precios</Label>
                            <Input value={editForm.pricing} onChange={(e) => setEditForm({...editForm, pricing: e.target.value})}
                              className="bg-gray-50 border border-gray-200 text-sm" data-testid="edit-bot-pricing" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Horario</Label>
                            <Input value={editForm.working_hours} onChange={(e) => setEditForm({...editForm, working_hours: e.target.value})}
                              className="bg-gray-50 border border-gray-200 text-sm" data-testid="edit-bot-hours" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 block">Mensaje de Bienvenida</Label>
                          <Input value={editForm.welcome_message} onChange={(e) => setEditForm({...editForm, welcome_message: e.target.value})}
                            className="bg-gray-50 border border-gray-200 text-sm" data-testid="edit-bot-welcome" />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button onClick={handleSaveBot} disabled={savingBot} data-testid="save-bot-edit-btn"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg">
                            {savingBot ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                          </Button>
                          <Button variant="outline" onClick={() => setEditingBot(null)} className="text-sm px-4 py-2 rounded-lg"
                            data-testid="cancel-bot-edit-btn">
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-gray-100 pt-3">
                        <p className="text-xs text-gray-400 mb-1">Prompt:</p>
                        <p className="text-sm text-gray-700 line-clamp-2 mb-2">{bot.bot_prompt ? bot.bot_prompt.substring(0, 150) + (bot.bot_prompt.length > 150 ? '...' : '') : 'Sin prompt'}</p>
                        <Button variant="outline" size="sm" onClick={() => handleEditBot(bot)}
                          data-testid={`edit-bot-btn-${bot.bot_id}`}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 text-xs">
                          Editar Configuracion
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Capacity Tab */}
        {activeTab === 'capacity' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="capacity-settings">
              <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2"><Settings className="w-5 h-5 text-gray-400" />Configuracion de Capacidad</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-700 font-medium text-sm mb-2 block">Maximo Asistentes Activos Simultaneos</Label>
                  <div className="flex gap-3 items-center">
                    <Input type="number" value={maxBots} onChange={(e) => setMaxBots(parseInt(e.target.value) || 0)} data-testid="max-bots-input" className="w-32 bg-white border-gray-200 rounded-lg" />
                    <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn"
                      className="bg-indigo-600 text-white hover:bg-indigo-700">
                      {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Actualmente: {stats.active_bots} activos de {maxBots} permitidos</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Los asistentes que no reciben mensajes por <strong>2 horas</strong> se pausan automaticamente.</p>
                  <p className="text-sm text-gray-600">Se reactivan solos cuando llega un nuevo mensaje.</p>
                </div>
              </div>
            </div>

            {/* Waiting List */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="waiting-list-section">
              <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-400" />Lista de Espera
                <span className="text-sm font-normal text-gray-500">({waitingList.length} usuarios)</span>
              </h3>
              {waitingList.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay usuarios en lista de espera.</p>
              ) : (
                <div className="space-y-2">
                  {waitingList.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm text-gray-700 font-mono">{entry.user_email}</span>
                        <span className="text-xs text-gray-400 ml-3">{formatDate(entry.created_at)}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveFromWaitlist(entry.user_email)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-4" data-testid="audit-log-section">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-950 flex items-center gap-2">
                <Shield className="w-5 h-5 text-gray-400" />Log de Auditoria de Prompts
              </h3>
              <Button onClick={fetchAuditLogs} disabled={auditLoading} size="sm" className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="refresh-audit-btn">
                {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
              </Button>
            </div>

            {auditLogs.length === 0 && !auditLoading ? (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="audit-empty">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay registros de cambios de prompt aun.</p>
                <p className="text-gray-400 text-sm mt-1">Haz click en "Actualizar" para cargar los registros.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={`audit-entry-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono text-gray-700">{log.user_email}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.source === 'bot_config' ? 'bg-blue-100 text-blue-700' :
                          log.source === 'alex_apply_new_bot' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {log.source === 'bot_config' ? 'Config Manual' :
                           log.source === 'alex_apply_new_bot' ? 'Alex (nuevo bot)' :
                           'Alex (actualizar)'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{formatDateTime(log.timestamp)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {log.old_prompt && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                          <p className="text-xs font-semibold text-red-600 mb-1">Prompt anterior:</p>
                          <p className="text-xs text-red-800 whitespace-pre-wrap max-h-24 overflow-y-auto">{log.old_prompt.substring(0, 300)}{log.old_prompt.length > 300 ? '...' : ''}</p>
                        </div>
                      )}
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <p className="text-xs font-semibold text-emerald-600 mb-1">Prompt nuevo:</p>
                        <p className="text-xs text-emerald-800 whitespace-pre-wrap max-h-24 overflow-y-auto">{log.new_prompt.substring(0, 300)}{log.new_prompt.length > 300 ? '...' : ''}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Bot: {log.bot_id}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
